import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormNavigationProvider, NavTextField } from '../components/FormNavigation';
import { Add, Block, ContentCopy } from '@mui/icons-material';
import DashboardLayout from '../components/DashboardLayout';
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  type ApiKeyItem,
  type CreateApiKeyResponse,
} from '../services/apiKeys';

const ApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKeyItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);

  const [revealedKey, setRevealedKey] = useState<CreateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const createContainerRef = React.useRef<HTMLElement | null>(null);
  const revealedContainerRef = React.useRef<HTMLElement | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<ApiKeyItem | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      setKeys(await listApiKeys());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const result = await createApiKey(newLabel.trim());
      setRevealedKey(result);
      setCreateOpen(false);
      setNewLabel('');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  }

  async function handleRevoke() {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await revokeApiKey(revokeTarget.id);
      setRevokeTarget(null);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to revoke API key');
    } finally {
      setRevoking(false);
    }
  }

  function handleCopy() {
    if (!revealedKey) return;
    navigator.clipboard.writeText(revealedKey.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">API Keys</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Generate New Key
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No API keys yet</TableCell>
                  </TableRow>
                )}
                {keys.map((k) => (
                  <TableRow key={k.id}>
                    <TableCell>{k.label}</TableCell>
                    <TableCell>{k.createdBy}</TableCell>
                    <TableCell>{new Date(k.createdAt).toLocaleString()}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={k.isActive ? 'success.main' : 'text.disabled'}
                      >
                        {k.isActive ? 'Active' : 'Revoked'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {k.isActive && (
                        <Tooltip title="Revoke key">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRevokeTarget(k)}
                          >
                            <Block fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ ref: createContainerRef }}
      >
        <FormNavigationProvider containerRef={createContainerRef}>
          <DialogTitle>Generate New API Key</DialogTitle>
          <DialogContent>
            <NavTextField
              autoFocus
              fullWidth
              label="Label"
              placeholder="e.g. Biometric Software - Main Office"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating || !newLabel.trim()}
            >
              {creating ? <CircularProgress size={18} /> : 'Generate'}
            </Button>
          </DialogActions>
        </FormNavigationProvider>
      </Dialog>

      {/* Revealed key dialog — shown only once */}
      <Dialog
        open={!!revealedKey}
        maxWidth="sm"
        fullWidth
        PaperProps={{ ref: revealedContainerRef }}
      >
        <FormNavigationProvider containerRef={revealedContainerRef}>
          <DialogTitle>API Key Generated</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This key will never be shown again. Copy it now and store it securely.
            </Alert>
            <NavTextField
              fullWidth
              value={revealedKey?.apiKey ?? ''}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                      <IconButton onClick={handleCopy} edge="end">
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={() => { setRevealedKey(null); setCopied(false); }}>
              Done
            </Button>
          </DialogActions>
        </FormNavigationProvider>
      </Dialog>

      {/* Revoke confirmation dialog */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Revoke <strong>{revokeTarget?.label}</strong>? Any software using this key will immediately lose access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRevoke} disabled={revoking}>
            {revoking ? <CircularProgress size={18} /> : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default ApiKeysPage;
